import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import callApi from "helpers/callApi";
import { buildQuery } from "EinvoiceProject/E-Documents/utils/buildQuery";
import { formatValidationErrorsFromBody, parseServerError } from "utils/errors";

const trimOrEmpty = (v) => (typeof v === "string" ? v.trim() : "");

function deleteCustomerFailureMessage(payload, actionError) {
	if (typeof payload === "string" && payload.trim()) return payload;
	if (payload && typeof payload === "object") {
		const validated = formatValidationErrorsFromBody(payload);
		if (validated) return validated;
		const direct = payload.error ?? payload.message;
		if (typeof direct === "string" && direct.trim()) return direct;
	}
	const fallback = parseServerError(actionError);
	return typeof fallback === "string" && fallback.trim() ? fallback : "Error";
}

export const fetchCustomers = createAsyncThunk(
	"customers/fetchCustomers",
	async (params = {},
		{rejectWithValue, getState}) => {
		try {
			const state = getState().customers;
			const limit = params.limit ?? state.limit ?? 5;
			const offset = params.offset ?? state.offset ?? 0;
			const filters = state.customersListFilters || {};

			const queryObj = {
				limit,
				offset,
				...(trimOrEmpty(filters.search) ? { search: trimOrEmpty(filters.search) } : {}),
				...(trimOrEmpty(filters.name) ? { name: trimOrEmpty(filters.name) } : {}),
				...(trimOrEmpty(filters.email) ? { email: trimOrEmpty(filters.email) } : {}),
				...(trimOrEmpty(filters.country_code) ? { country_code: trimOrEmpty(filters.country_code) } : {}),
				...(trimOrEmpty(filters.contact_person) ? { contact_person: trimOrEmpty(filters.contact_person) } : {}),
			};

			const qs = buildQuery(queryObj);

			const response = await callApi(`/documents/customers${qs}`, "GET");
			if (!response.ok) {
				throw new Error(response.errorMessage || "Error");
			}
			const data = await response.json();
			return { data, limit, offset };
		} catch (error) {
			return rejectWithValue(error?.message || "Error");
		}
	}
);

export const getLanguagesList = createAsyncThunk(
	"customers/getLanguagesList",
	async (params,
		{rejectWithValue}) => {
		try {
			const response = await callApi("/peppol/languages", "GET");
			if (!response.ok) {
				throw new Error(response.errorMessage || "Error");
			}
			const data = await response.json();
			return data;
		} catch (error) {
			return rejectWithValue(error?.message || "Error");
		}
	}
);

export const getCountriesAllList = createAsyncThunk(
	"customers/getCountriesAllList",
	async (params,
		{rejectWithValue}) => {
		try {
			const response = await callApi("/countries_all", "GET");
			if (!response.ok) {
				throw new Error(response.errorMessage || "Error");
			}
			const data = await response.json();
			return data;
		} catch (error) {
			return rejectWithValue(error?.message || "Error");
		}
	}
);

export const addCustomer = createAsyncThunk(
	"customers/addCustomer",
	async (params,
		{rejectWithValue}) => {
		try {
			const response = await callApi("/documents/customer", "POST", params);
			if (!response.ok) {
				return rejectWithValue(response.errorMessage || "Error");
			}
			const data = await response.json();
			return data;
		} catch (error) {
			return rejectWithValue(error?.message || "Error");
		}
	}
);

export const editCustomer = createAsyncThunk(
	"customers/editCustomer",
	async (params,
		{rejectWithValue}) => {
		try {
			const response = await callApi("/documents/customer", "PUT", params);
			if (!response.ok) {
				return rejectWithValue(response.errorMessage || "Error");
			}
			const data = await response.json();
			return data;
		} catch (error) {
			return rejectWithValue(error?.message || "Error");
		}
	}
);

export const deleteCustomer = createAsyncThunk(
	"manager/deleteCustomer",
	async (id,
		{rejectWithValue}) => {
		try {
			const response = await callApi(`/documents/customer/${id}`, "DELETE");
			if (!response.ok) {
				return rejectWithValue({
					status: response.status,
					error: response.errorMessage || "Error",
				});
			}
			try {
				return await response.json();
			} catch {
				return null;
			}
		} catch (err) {
			return rejectWithValue({ error: err?.message || "Error" });
		}
	}
);

export const fetchCustomerSystemMessages = createAsyncThunk(
	"customers/fetchCustomerSystemMessages",
	async (_, { rejectWithValue }) => {
		try {
			const response = await callApi(
				"/notify/get_system_message_active?models_name=customers",
				"GET"
			);
			if (response.ok) {
				return await response.json();
			} else {
				return rejectWithValue(response.errorMessage || "Error");
			}
		} catch (err) {
			return rejectWithValue(err?.message || "Error");
		}
	}
);

const initialState = {
	customers: [],
	languages_list: [],
	countries_list: [],
	messages: [],
	limit: 25,
	offset: 0,
	total: 0,
	customersListFilters: {
		search: "",
		name: "",
		email: "",
		country_code: "",
		contact_person: "",
	},
	deleteCustomerError: null,
};

const customersSlice = createSlice({
	name: "customers",
	initialState,
	reducers: {
		clearCustomerMessages(state) {
			state.messages = [];
		},
		setCustomersLimit(state, action) {
			state.limit = action.payload;
		},
		setCustomersOffset(state, action) {
			state.offset = action.payload;
		},
		resetCustomersPagination(state) {
			state.offset = 0;
		},
		setCustomersListFilters(state, action) {
			const p = action.payload || {};
			state.customersListFilters = {
				search: p.search != null ? String(p.search) : "",
				name: p.name != null ? String(p.name) : "",
				email: p.email != null ? String(p.email) : "",
				country_code: p.country_code != null ? String(p.country_code) : "",
				contact_person: p.contact_person != null ? String(p.contact_person) : "",
			};
		},
		resetCustomersListFilters(state) {
			state.customersListFilters = {
				search: "",
				name: "",
				email: "",
				country_code: "",
				contact_person: "",
			};
		},
		clearDeleteCustomerError(state) {
			state.deleteCustomerError = null;
		},
	},
	extraReducers: (builder) =>
		builder
			.addCase(fetchCustomers.pending, (state, action) => {
				if (!action.meta.arg?.silent) {
					state.fetchingCustomers = true;
				}
			})
			.addCase(fetchCustomers.fulfilled, (state,
				action) => {
				const isSilent = action.meta.arg?.silent || false;
				if (!isSilent) {
					state.fetchingCustomers = false;
				}
				state.fetchingCustomersError = null;
				
				const responseData = action.payload?.data || action.payload;
				let customersArray = [];
				
				if (Array.isArray(responseData)) {
					customersArray = responseData;
				} else if (responseData && typeof responseData === 'object') {
					customersArray = responseData.items || responseData.data || [];
					if (!Array.isArray(customersArray)) {
						customersArray = [];
					}
				}
				
				state.customers = customersArray;
				state.total = responseData?.count || responseData?.total || 0;
				
				if (action.payload?.limit !== undefined) {
					state.limit = action.payload.limit;
				}
				if (action.payload?.offset !== undefined) {
					state.offset = action.payload.offset;
				}
			})
			.addCase(fetchCustomers.rejected, (state,
				action) => {
				state.fetchingCustomers = false;
				state.fetchingCustomersError = parseServerError(action.payload ?? action.error);
			})
			
			.addCase(getLanguagesList.pending, (state) => {
				state.getLanguagesList = true;
			})
			.addCase(getLanguagesList.fulfilled, (state,
				action) => {
				state.getLanguagesList = false;
				state.getLanguagesListError = null;
				state.languages_list = action.payload;
			})
			.addCase(getLanguagesList.rejected, (state,
				action) => {
				state.getLanguagesList = false;
				state.getLanguagesListError = parseServerError(action.payload ?? action.error);
			})
			
			.addCase(getCountriesAllList.pending, (state) => {
				state.getCountriesAllList = true;
			})
			.addCase(getCountriesAllList.fulfilled, (state,
				action) => {
				state.getCountriesAllList = false;
				state.getCountriesAllListError = null;
				state.countries_list = action.payload;
			})
			.addCase(getCountriesAllList.rejected, (state,
				action) => {
				state.getCountriesAllList = false;
				state.getCountriesAllListError = parseServerError(action.payload ?? action.error);
			})
			
			.addCase(addCustomer.pending, (state) => {
				state.addCustomerLoader = true;
			})
			.addCase(addCustomer.fulfilled, (state,
				action) => {
				state.addCustomerLoader = false;
				state.addCustomerError = null;
			})
		.addCase(addCustomer.rejected, (state,
			action) => {
			state.addCustomerLoader = false;
			state.addCustomerError = action.payload || parseServerError(action.payload ?? action.error);
		})
			
			.addCase(editCustomer.pending, (state) => {
				state.editCustomerLoader = true;
			})
			.addCase(editCustomer.fulfilled, (state,
				action) => {
				state.editCustomerLoader = false;
				state.editCustomerError = null;
			})
		.addCase(editCustomer.rejected, (state,
			action) => {
			state.editCustomerLoader = false;
			state.editCustomerError = action.payload || parseServerError(action.payload ?? action.error);
		})
			
			.addCase(deleteCustomer.pending, (state) => {
				state.deleteCustomerLoader = true;
				state.deleteCustomerError = null;
			})
			.addCase(deleteCustomer.fulfilled, (state,
				action) => {
				state.deleteCustomerLoader = false;
				state.deleteCustomerError = null;
			})
			.addCase(deleteCustomer.rejected, (state,
				action) => {
				state.deleteCustomerLoader = false;
				state.deleteCustomerError = deleteCustomerFailureMessage(
					action.payload,
					action.error
				);
			})
			.addCase(fetchCustomerSystemMessages.pending, (state) => {
				state.fetchCustomerSystemMessages = true;
				state.fetchCustomerSystemMessagesError = null;
			})
			.addCase(fetchCustomerSystemMessages.fulfilled, (state, action) => {
				state.fetchCustomerSystemMessages = false;
				state.fetchCustomerSystemMessagesError = null;
				state.messages = action.payload;
			})
			.addCase(fetchCustomerSystemMessages.rejected, (state, action) => {
				state.fetchCustomerSystemMessages = false;
				state.fetchCustomerSystemMessagesError = action.payload;
			})
});

export const {
	clearCustomerMessages,
	setCustomersLimit,
	setCustomersOffset,
	resetCustomersPagination,
	setCustomersListFilters,
	resetCustomersListFilters,
	clearDeleteCustomerError,
} = customersSlice.actions;
export default customersSlice.reducer;
